"use client";

import Link from "./next/Link";
import clsx from "clsx";
import { usePathname } from "next/navigation";
import {
  type ReactNode,
  type MouseEventHandler,
  type AnchorHTMLAttributes,
} from "react";
import { cn } from "~/utils/cn";

export default function CustomLink({
  to,
  className,
  children,
  label,
  childrenSubText = null,
  useRemixNavLink = false,
  activeLinkClasses = "",
  ...rest
}: {
  to?: string | null;
  className?: string;
  children: ReactNode | [];
  label?: string;
  childrenSubText?: string | null;
  useRemixNavLink?: boolean;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  activeLinkClasses?: string;
} & AnchorHTMLAttributes<HTMLAnchorElement>) {
  const pathname = usePathname();

  // This can be either markup or a Prismic object, if its prismic we parse it
  const content = children;

  // If we dont have a link or dont have the link text, we don't render the link
  if (!to || !content) return null;

  // For external links, we just paste it in a normal link tag.

  // TODO: Revert back once https://github.com/vercel/next.js/issues/55990 is fixed. Maybe try upgrading next version.
  if (to.includes("http")) {
    return (
      <a
        href={to}
        target={to.includes("http") ? "_blank" : undefined}
        rel="noreferrer noopener"
        className={cn(className, pathname === to && activeLinkClasses)}
        aria-label={label && label}
        {...rest}
      >
        {content}
        {childrenSubText && (
          <span className="ml-1 inline-block -translate-y-1 text-tiny-body text-white/80">
            {childrenSubText}
          </span>
        )}
      </a>
    );
  }

  if (useRemixNavLink) {
    return (
      <Link
        href={to}
        className={clsx(className, pathname === to && activeLinkClasses)}
        aria-label={label}
        {...rest}
      >
        {content}
        {childrenSubText && (
          <span className="ml-1 inline-block -translate-y-1 text-tiny-body text-white/80">
            {childrenSubText}
          </span>
        )}
      </Link>
    );
  }

  return (
    <Link href={to} className={className} aria-label={label} {...rest}>
      {content}
      {childrenSubText && (
        <span className="ml-4 inline-block -translate-y-1 text-tiny-body text-white/80">
          {childrenSubText}
        </span>
      )}
    </Link>
  );
}
